import React from 'react';
import { ChevronRight, ChevronDown, FileText, BookOpen, Upload, Link2, Plus, FileUp, Trash2, Unlink, Eye, Lightbulb, FileType, FileSpreadsheet, File, FolderOpen, GitCompare } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ProjectFile, ProjectPlaybook } from '../../services/Project';
import { ExportFileOptions, QAgentSuggestion } from '../../services/QAgent';
import { SuggestionsSummary } from './suggestions';
import { useI18n } from '../../i18n';

interface ContextPanelProps {
  projectId: string | null;
  projectName: string | null;
  files: ProjectFile[];
  playbooks: ProjectPlaybook[];
  activeFileIds: string[];
  activePlaybookId: string | null;
  onSelectPlaybook: (playbookId: string | null) => void;
  onUploadClick: () => void;
  onLinkPlaybookClick: () => void;
  onCreatePlaybookClick: () => void;
  onUploadPlaybookClick: () => void;
  onDeleteFile: (file: ProjectFile) => void;
  onViewFile: (file: ProjectFile) => void;
  /** Compare a file with a standard agreement (editors only) */
  onCompareFile?: (file: ProjectFile) => void;
  onViewPlaybook: (playbook: ProjectPlaybook) => void;
  onUnlinkPlaybook: (playbook: ProjectPlaybook) => void;
  isLoading?: boolean;
  // Section collapse state (controlled from parent to persist across sidebar toggles)
  filesExpanded: boolean;
  playbooksExpanded: boolean;
  onFilesExpandedChange: (expanded: boolean) => void;
  onPlaybooksExpandedChange: (expanded: boolean) => void;
  // Suggestions
  suggestions?: QAgentSuggestion[];
  suggestionsExpanded?: boolean;
  onSuggestionsExpandedChange?: (expanded: boolean) => void;
  onAcceptAllSuggestions?: () => void;
  onRejectAllSuggestions?: () => void;
  onViewSuggestion?: (suggestion: QAgentSuggestion) => void;
  onExportFileWithEdits?: (fileId: string, fileName: string, options: ExportFileOptions) => void;
  exportingFileId?: string | null;
  loadingSuggestions?: boolean;
}

export const ContextPanel: React.FC<ContextPanelProps> = ({
  projectId,
  projectName,
  files,
  playbooks,
  activeFileIds,
  activePlaybookId,
  onSelectPlaybook,
  onUploadClick,
  onLinkPlaybookClick,
  onCreatePlaybookClick,
  onUploadPlaybookClick,
  onDeleteFile,
  onViewFile,
  onCompareFile,
  onViewPlaybook,
  onUnlinkPlaybook,
  isLoading,
  filesExpanded,
  playbooksExpanded,
  onFilesExpandedChange,
  onPlaybooksExpandedChange,
  suggestions = [],
  suggestionsExpanded = true,
  onSuggestionsExpandedChange,
  onAcceptAllSuggestions,
  onRejectAllSuggestions,
  onViewSuggestion,
  onExportFileWithEdits,
  exportingFileId,
  loadingSuggestions,
}) => {
  const { t } = useI18n();
  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  const hasSuggestions = suggestions.length > 0;
  if (!projectId) {
    return null;
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) {
      return <FileText className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />;
    }
    if (mimeType.includes('word') || mimeType.includes('docx')) {
      return <FileType className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />;
    }
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
      return <FileSpreadsheet className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />;
    }
    return <File className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />;
  };

  return (
    <div className="h-full flex flex-col bg-muted/30 overflow-hidden" style={{ minWidth: 240 }}>
      {/* Project Header */}
      <div className="p-3 border-b flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen className="h-4 w-4 text-brand flex-shrink-0" />
          <span className="font-medium text-sm truncate">{t.qagentDocument.contextPanel.projectResources}</span>
        </div>
      </div>

      {/* Radix wraps content in a display:table div that grows to the widest
          row, which breaks truncation and pushes row actions out of view */}
      <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
        <div className="p-2 space-y-2">
          {/* Files Section */}
          <Collapsible open={filesExpanded} onOpenChange={onFilesExpandedChange}>
            <div className="flex items-center gap-2 w-full p-2 hover:bg-muted rounded">
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 flex-1 min-w-0">
                  {filesExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <FileText className="h-4 w-4 text-brand flex-shrink-0" />
                  <span className="font-semibold text-sm">{t.qagentDocument.contextPanel.files.title}</span>
                  <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                    {files.length}
                  </Badge>
                </button>
              </CollapsibleTrigger>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground"
                onClick={onUploadClick}
                title={t.qagentDocument.contextPanel.files.uploadTooltip}
              >
                <Upload className="h-3.5 w-3.5" />
              </Button>
            </div>
            <CollapsibleContent>
              <div className="pl-3 space-y-1">
                {isLoading ? (
                  <div className="p-2 text-xs text-muted-foreground">{t.common.states.loading}</div>
                ) : files.length === 0 ? (
                  <div className="p-3 text-center">
                    <FileText className="h-5 w-5 mx-auto mb-1.5 text-muted-foreground/40" />
                    <p className="text-xs text-muted-foreground mb-2">{t.qagentDocument.contextPanel.files.empty}</p>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onUploadClick}>
                      <Upload className="h-3 w-3 mr-1" />
                      {t.qagentDocument.contextPanel.files.uploadButton}
                    </Button>
                  </div>
                ) : (
                  files.map((file) => {
                    return (
                      <div
                        key={file.id}
                        className="group flex items-center gap-2 p-1.5 rounded hover:bg-muted transition-colors min-w-0"
                      >
                        <span className="text-xs truncate min-w-0 flex-1" title={file.name}>
                          {file.name}
                        </span>
                        {file.status === 'processing' && (
                          <span className="text-xs text-muted-foreground flex-shrink-0">{t.qagentDocument.contextPanel.files.processing}</span>
                        )}
                        {onCompareFile && file.status === 'ready' && /\.(docx|pdf)$/i.test(file.name) && (
                          <button
                            className="opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 p-0.5 hover:bg-muted-foreground/10 rounded transition-opacity flex-shrink-0"
                            onClick={() => onCompareFile(file)}
                            title={t.comparisons.compareFile}
                            aria-label={t.comparisons.compareFile}
                          >
                            <GitCompare className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        )}
                        <button
                          className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-0.5 hover:bg-muted-foreground/10 rounded transition-opacity flex-shrink-0"
                          onClick={() => onViewFile(file)}
                          title={t.qagentDocument.contextPanel.files.viewTooltip}
                        >
                          <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                        <button
                          className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-0.5 hover:bg-destructive/10 rounded transition-opacity flex-shrink-0"
                          onClick={() => onDeleteFile(file)}
                          title={t.qagentDocument.contextPanel.files.deleteTooltip}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Playbooks Section */}
          <Collapsible open={playbooksExpanded} onOpenChange={onPlaybooksExpandedChange}>
            <div className="flex items-center gap-1 w-full p-2 hover:bg-muted rounded">
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 flex-1 min-w-0">
                  {playbooksExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <BookOpen className="h-4 w-4 text-brand flex-shrink-0" />
                  <span className="font-semibold text-sm">{t.qagentDocument.contextPanel.playbooks.title}</span>
                  <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                    {playbooks.length}
                  </Badge>
                </button>
              </CollapsibleTrigger>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={onLinkPlaybookClick}
                  title={t.qagentDocument.contextPanel.playbooks.linkTooltip}
                >
                  <Link2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={onCreatePlaybookClick}
                  title={t.qagentDocument.contextPanel.playbooks.createTooltip}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={onUploadPlaybookClick}
                  title={t.qagentDocument.contextPanel.playbooks.uploadTooltip}
                >
                  <FileUp className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <CollapsibleContent>
              <div className="pl-3 space-y-1">
                {playbooks.length === 0 ? (
                  <div className="p-3 text-center">
                    <BookOpen className="h-5 w-5 mx-auto mb-1.5 text-muted-foreground/40" />
                    <p className="text-xs text-muted-foreground mb-2">{t.qagentDocument.contextPanel.playbooks.empty}</p>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onLinkPlaybookClick}>
                      <Link2 className="h-3 w-3 mr-1" />
                      {t.qagentDocument.contextPanel.playbooks.linkButton}
                    </Button>
                  </div>
                ) : (
                  playbooks.map((playbook) => (
                    <div
                      key={playbook.id}
                      className="group flex items-center gap-2 w-full p-1.5 rounded text-left hover:bg-muted transition-colors min-w-0"
                    >
                      <span
                        className="text-xs truncate min-w-0 flex-1"
                        title={playbook.playbookName}
                      >
                        {playbook.playbookName}
                      </span>
                      <button
                        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-muted-foreground/10 rounded transition-opacity flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewPlaybook(playbook);
                        }}
                        title={t.qagentDocument.contextPanel.playbooks.viewTooltip}
                      >
                        <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      <button
                        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-destructive/10 rounded transition-opacity flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUnlinkPlaybook(playbook);
                        }}
                        title={t.qagentDocument.contextPanel.playbooks.unlinkTooltip}
                      >
                        <Unlink className="h-3.5 w-3.5 text-destructive" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Suggestions Section */}
          {hasSuggestions && (
            <Collapsible open={suggestionsExpanded} onOpenChange={onSuggestionsExpandedChange}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 w-full p-2 hover:bg-muted rounded">
                  {suggestionsExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <Lightbulb className="h-4 w-4 text-brand flex-shrink-0" />
                  <span className="font-semibold text-sm">{t.qagentDocument.suggestions.title}</span>
                  <Badge variant="secondary" className="ml-auto text-xs px-1.5 py-0.5">
                    {t.qagentDocument.suggestions.pendingBadge(pendingSuggestions.length)}
                  </Badge>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="pl-3 pr-2 pb-2">
                  <SuggestionsSummary
                    suggestions={suggestions}
                    onAcceptAll={onAcceptAllSuggestions || (() => {})}
                    onRejectAll={onRejectAllSuggestions || (() => {})}
                    onViewSuggestion={onViewSuggestion || (() => {})}
                    onExportFile={onExportFileWithEdits}
                    exportingFileId={exportingFileId}
                    isLoading={loadingSuggestions}
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </ScrollArea>

      {/* Active Context Summary */}
      {(activeFileIds.length > 0 || activePlaybookId) && (
        <div className="p-3 border-t bg-brand-light/50 flex-shrink-0">
          <div className="text-xs text-muted-foreground truncate">
            {activeFileIds.length > 0 && (
              <span>{t.qagentDocument.contextPanel.activeContext.files(activeFileIds.length)}</span>
            )}
            {activeFileIds.length > 0 && activePlaybookId && <span> · </span>}
            {activePlaybookId && (
              <span className="text-brand">
                @{playbooks.find(p => p.playbookId === activePlaybookId)?.playbookName}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextPanel;
